import React, { useEffect, useRef, useState } from "react";
import FormInput from "../../../../FormInput";
import Password from "../../../../Password";
import FormLocationInput from "../../../../FormLocationInput";
import { useFormikContext } from "formik";
import { getCityAndState } from "../../../../../utils";
import FormSelect from "../../../../FormSelect";
import { getCitiesByStateId } from "../../../../../services/customer/dropdown";
import FormCheckboxGroup from "../../../../FormCheckboxGroup";
import { CUSTOMER_TYPES, SELLER_TYPES } from "../../../../../constants";
import { Option } from "../../../../../types";
import SvgIcon from "../../../../../assets/icons/SvgIcon";

interface IProps {
  handleLocationDetails: (
    addressDetails: google.maps.places.PlaceResult
  ) => void;
  addressDetails: google.maps.places.PlaceResult | null;
  statesList: any[];
}

const RegistrationForm: React.FC<IProps> = ({
  addressDetails,
  handleLocationDetails,
  statesList,
}) => {
  const maxPasswordLength = 16;
  const { values, setFieldValue, errors }: any = useFormikContext();
  const cityListRef = useRef<Option[]>([]);

  const [cityList, setCityList] = useState([]);
  const [passwordErrors, setPasswordErrors] = useState({
    minValueValidation: false,
    capitalSmallLetterValidation: false,
    numberValidation: false,
    specialCharacterValidation: false,
  });

  const validatePassword = (password: string) => {
    setPasswordErrors({
      minValueValidation:
        password.length >= 8 && password.length <= maxPasswordLength,
      capitalSmallLetterValidation: /(?=.*[a-z])(?=.*[A-Z])/.test(password),
      numberValidation: /\d/.test(password),
      specialCharacterValidation: /[^A-Za-z0-9]/.test(password),
    });
  };

  const handleCity = (city: any) => {
    const cityList = city?.map((item: any) => ({
      value: item._id,
      label: item.name,
    }));
    cityListRef.current = cityList;
    setCityList(cityList);
  };

  useEffect(() => {
    if (values?.state_id && values?.state_id !== "") {
      getCitiesByStateId(values?.state_id?.value, handleCity);
    } else {
      setCityList([]);
    }
  }, [values?.state_id]);

  useEffect(() => {
    if (addressDetails) {
      const { postalCode, state, city } = getCityAndState(
        addressDetails?.address_components
      );

      if (state) {
        const selectState = statesList.find(
          (item: Option) => item.label === state
        );
        setFieldValue("state_id", selectState);
        setFieldValue("city_id", "");
      }

      if (city) {
        setTimeout(() => {
          const newCityList = cityListRef?.current || cityList;
          const selectCity = newCityList?.find(
            (item: Option) => item.label === city
          );

          setFieldValue("city_id", selectCity ?? "");
        }, 500);
      }

      if (postalCode) {
        setFieldValue("postal_code", postalCode);
      }
    }
  }, [addressDetails]);

  return (
    <div>
      <FormInput
        label="Email"
        name="email"
        placeholder="enter email"
        required
      />
      <Password
        label="New Password"
        name="password"
        required
        placeholder="enter password"
        handleChange={(event) => {
          validatePassword(event?.target?.value);
        }}
      />
      <Password
        label="Confirm Password"
        name="confirmPassword"
        required
        placeholder="enter confirm password"
      />
      <div className="mt-8 text-sm">
        {Object.entries(passwordErrors).map(([key, value]) => (
          <div key={key} className="flex items-center gap-4 my-1">
            <div>
              {value ? (
                <SvgIcon iconName="Check" className="ml-[2px] check-icon" />
              ) : (
                <SvgIcon iconName="close" className="close-icon" />
              )}
            </div>
            <p className={`${value ? "text-green-500" : "text-red-500"}`}>
              {key === "minValueValidation" &&
                "Password must be between 8-16 Characters"}
              {key === "capitalSmallLetterValidation" &&
                "Password must have at least one Capital and one Small Letter"}
              {key === "numberValidation" &&
                "Password must have at least one Number"}
              {key === "specialCharacterValidation" &&
                "Password must have at least one Special Character"}
            </p>
          </div>
        ))}
      </div>
      <div className="mt-8 information">
        <div className="flex small-xs:flex-row flex-col gap-4">
          <div className="font-semibold">Join As</div>
          <FormCheckboxGroup
            type="radio"
            name="role"
            required
            options={CUSTOMER_TYPES}
          />
        </div>
        {Number((values as any)?.role) === 5 && (
          <FormCheckboxGroup
            label="Seller Type"
            type="radio"
            name="seller_type"
            required
            optionClassName="mr-2"
            options={SELLER_TYPES}
            labelClassName="block font-semibold mb-1"
            error={errors?.seller_type}
          />
        )}
        <FormInput
          label="Full Name"
          name="full_name"
          placeholder="enter full name"
          required
        />
        <FormLocationInput
          name="address"
          label="Address (Select from dropdown only)"
          placeholder="enter address"
          required
          handleDetails={handleLocationDetails}
          onBlur={() => {
            if (!addressDetails?.formatted_address) {
              setFieldValue("address", "");
            }
          }}
          className="!pl-8"
          customElement={
            <div className="top-4 left-3 absolute">
              <SvgIcon iconName="locationDot" className={`size-4`} />
            </div>
          }
        />
        <div className="lg:flex items-center gap-x-5 mb-2">
          <FormSelect
            label="State"
            name="state_id"
            options={statesList}
            placeholder="select state"
            className="mb-4 lg:mb-2"
            required
            handleChange={() => {
              setFieldValue("city_id", "");
            }}
          />
          <FormSelect
            label="City"
            name="city_id"
            placeholder="select city"
            options={cityList}
            className="mb-4 lg:mb-2"
            required
          />
        </div>
        <FormInput
          type="number"
          label="Zip/Postal code"
          name="postal_code"
          required
          maxLength={6}
          placeholder="enter zip/ postal code"
        />
        <FormInput
          type="number"
          label="Phone Number"
          name="contact_number"
          required
          placeholder="enter phone number"
          maxLength={10}
        />
      </div>
    </div>
  );
};

export default RegistrationForm;
